import { useLayoutEffect, useMemo, useRef, useState } from "react";

export const PV_TABLE_BORDER = "1px solid rgba(210, 218, 230, 1)";

export const PV_TABLE_HEADER_BACKGROUND = "hsla(250, 86%, 97%, 1)";

export const PV_TABLE_HEADER_HEIGHT = 72;
export const PV_TABLE_ROW_HEIGHT = 38;

const getColumnId = (column) => String(column?.id || column?.accessorKey || "");

const applyResponsiveColumnWidth = (
  column,
  widthPx,
  headerFontSize,
  bodyFontSize,
) => {
  const safeWidth = Math.max(1, widthPx);

  const currentHeadProps =
    column?.muiTableHeadCellProps &&
    typeof column.muiTableHeadCellProps === "object"
      ? column.muiTableHeadCellProps
      : {};

  const currentBodyProps =
    column?.muiTableBodyCellProps &&
    typeof column.muiTableBodyCellProps === "object"
      ? column.muiTableBodyCellProps
      : {};

  return {
    ...column,

    // Override old fixed sizes from NozzleColumns
    size: safeWidth,
    minSize: safeWidth,
    maxSize: safeWidth,
    grow: false,

    muiTableHeadCellProps: {
      ...currentHeadProps,

      sx: {
        ...(currentHeadProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        height: `${PV_TABLE_HEADER_HEIGHT}px !important`,
        minHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,
        maxHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,

        borderRight: `${PV_TABLE_BORDER} !important`,
        borderBottom: `${PV_TABLE_BORDER} !important`,

        backgroundColor: PV_TABLE_HEADER_BACKGROUND,

        overflow: "hidden",

        textAlign: "center",

        px: {
          xs: 0,
          sm: 0.1,
          md: 0.25,
        },

        fontSize: `${headerFontSize}px !important`,
        fontWeight: "700 !important",

        "& .Mui-TableHeadCell-Content": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
        },

        "& .Mui-TableHeadCell-Content-Labels": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          overflow: "hidden",
        },

        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          minWidth: "0 !important",
          maxWidth: "100%",

          textAlign: "center",

          whiteSpace: "normal !important",
          overflowWrap: "normal",
          wordBreak: "normal",

          overflow: "hidden !important",

          fontSize: `${headerFontSize}px !important`,
          fontWeight: "700 !important",
          lineHeight: "1.15 !important",
        },

        "& .Mui-TableHeadCell-Content-Wrapper > *": {
          width: "100% !important",
          minWidth: "0 !important",
          maxWidth: "100% !important",

          textAlign: "center !important",
        },
      },
    },

    muiTableBodyCellProps: {
      ...currentBodyProps,

      sx: {
        ...(currentBodyProps.sx || {}),

        boxSizing: "border-box",

        width: `${safeWidth}px !important`,
        minWidth: `${safeWidth}px !important`,
        maxWidth: `${safeWidth}px !important`,

        flex: `0 0 ${safeWidth}px !important`,
        flexBasis: `${safeWidth}px !important`,
        flexGrow: "0 !important",
        flexShrink: "0 !important",

        borderRight: `${PV_TABLE_BORDER} !important`,

        overflow: "hidden",

        fontSize: `${bodyFontSize}px !important`,
      },
    },
  };
};

export const useResponsivePvTable = (rawColumns, columnWeights = {}) => {
  const viewportRef = useRef(null);

  const [tableWidth, setTableWidth] = useState(700);

  useLayoutEffect(() => {
    let frameId = null;

    const updateWidth = () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      frameId = requestAnimationFrame(() => {
        const element = viewportRef.current;

        if (!element) return;

        const rect = element.getBoundingClientRect();

        const viewportWidth = document.documentElement.clientWidth;

        /*
         * This works both when:
         *
         * 1. the table is half-screen beside Nozzles
         * 2. the table moves below Nozzles on a narrow screen
         *
         * We only use the actually visible width.
         */
        const visibleLeft = Math.max(rect.left, 0);

        const visibleRight = Math.min(rect.right, viewportWidth);

        const nextWidth = Math.max(
          1,
          Math.floor(visibleRight - visibleLeft - 1),
        );

        setTableWidth((previous) =>
          previous === nextWidth ? previous : nextWidth,
        );
      });
    };

    updateWidth();

    window.addEventListener("resize", updateWidth);

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;

    if (resizeObserver && viewportRef.current) {
      resizeObserver.observe(viewportRef.current);

      if (viewportRef.current.parentElement) {
        resizeObserver.observe(viewportRef.current.parentElement);
      }
    }

    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }

      window.removeEventListener("resize", updateWidth);

      resizeObserver?.disconnect();
    };
  }, []);

  const headerFontSize = useMemo(
    () => Math.max(7, Math.min(14, tableWidth / 70)),
    [tableWidth],
  );

  const bodyFontSize = useMemo(
    () => Math.max(7, Math.min(14, tableWidth / 70)),
    [tableWidth],
  );

  const columns = useMemo(() => {
    const totalWeight = rawColumns.reduce((sum, column) => {
      const columnId = getColumnId(column);

      return sum + (columnWeights[columnId] ?? 1);
    }, 0);

    const usableWidth = Math.max(1, tableWidth - 2);

    return rawColumns.map((column) => {
      const columnId = getColumnId(column);

      const weight = columnWeights[columnId] ?? 1;

      const width = (weight / totalWeight) * usableWidth;

      return applyResponsiveColumnWidth(
        column,
        width,
        headerFontSize,
        bodyFontSize,
      );
    });
  }, [rawColumns, columnWeights, tableWidth, headerFontSize, bodyFontSize]);

  return {
    viewportRef,
    tableWidth,
    headerFontSize,
    bodyFontSize,
    columns,
  };
};

export const getResponsivePvTableProps = ({
  tableWidth,
  headerFontSize,
  bodyFontSize,
}) => ({
  layoutMode: "grid-no-grow",

  defaultColumn: {
    size: 1,
    minSize: 1,
    maxSize: 100000,
    grow: false,
  },

  muiTableProps: {
    sx: {
      width: `${tableWidth}px !important`,
      minWidth: `${tableWidth}px !important`,
      maxWidth: `${tableWidth}px !important`,

      overflow: "hidden !important",
    },
  },

  muiTablePaperProps: {
    sx: {
      width: `${tableWidth}px !important`,
      minWidth: `${tableWidth}px !important`,
      maxWidth: `${tableWidth}px !important`,

      overflowX: "hidden !important",
    },
  },

  muiTableContainerProps: {
    sx: {
      width: `${tableWidth}px !important`,
      minWidth: `${tableWidth}px !important`,
      maxWidth: `${tableWidth}px !important`,

      overflowX: "hidden !important",

      "& .MuiTable-root": {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,
      },

      "& .MuiTableHead-root, & .MuiTableBody-root": {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        overflow: "hidden",
      },

      "& .MuiTableRow-root": {
        width: `${tableWidth}px !important`,
        minWidth: `${tableWidth}px !important`,
        maxWidth: `${tableWidth}px !important`,

        overflow: "hidden",
      },

      "& .MuiTableCell-root": {
        minWidth: "0 !important",
        boxSizing: "border-box",
        overflow: "hidden",
      },

      /*
       * Editing controls cannot force
       * a column wider.
       */
      "& .MuiTextField-root, & .MuiFormControl-root": {
        width: "100%",
        minWidth: "0 !important",
        maxWidth: "100%",
      },

      "& .MuiInputBase-root": {
        width: "100%",
        minWidth: "0 !important",
        maxWidth: "100%",

        overflow: "hidden",
      },

      "& .MuiInputBase-input": {
        width: "100%",
        minWidth: "0 !important",
        maxWidth: "100%",

        boxSizing: "border-box",

        fontSize: `${bodyFontSize}px !important`,
      },

      "& .MuiSelect-select": {
        width: "100%",
        minWidth: "0 !important",
        maxWidth: "100%",

        overflow: "hidden !important",
        textOverflow: "ellipsis",
      },
    },
  },

  muiTableHeadRowProps: {
    sx: {
      display: "flex !important",

      width: `${tableWidth}px !important`,
      minWidth: `${tableWidth}px !important`,
      maxWidth: `${tableWidth}px !important`,

      height: `${PV_TABLE_HEADER_HEIGHT}px !important`,
      minHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,
      maxHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,

      alignItems: "stretch",

      overflow: "hidden",

      backgroundColor: PV_TABLE_HEADER_BACKGROUND,
    },
  },

  muiTableHeadCellProps: {
    sx: {
      boxSizing: "border-box",

      height: `${PV_TABLE_HEADER_HEIGHT}px !important`,
      minHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,
      maxHeight: `${PV_TABLE_HEADER_HEIGHT}px !important`,

      borderRight: `${PV_TABLE_BORDER} !important`,

      backgroundColor: PV_TABLE_HEADER_BACKGROUND,

      overflow: "hidden",

      textAlign: "center",

      fontSize: `${headerFontSize}px !important`,

      fontWeight: "700 !important",

      "& .Mui-TableHeadCell-Content": {
        width: "100%",
        minWidth: 0,

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },

      "& .Mui-TableHeadCell-Content-Wrapper": {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",

        textAlign: "center",

        whiteSpace: "normal",

        overflow: "hidden",

        fontSize: `${headerFontSize}px !important`,
      },

      "& .Mui-TableHeadCell-Content-Wrapper > *": {
        width: "100% !important",
        textAlign: "center !important",
      },
    },
  },

  muiTableBodyRowProps: {
    sx: {
      display: "flex !important",

      width: `${tableWidth}px !important`,
      minWidth: `${tableWidth}px !important`,
      maxWidth: `${tableWidth}px !important`,

      height: `${PV_TABLE_ROW_HEIGHT}px !important`,
      minHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,
      maxHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,

      alignItems: "stretch",

      overflow: "hidden",

      "& .MuiTableCell-root": {
        height: `${PV_TABLE_ROW_HEIGHT}px !important`,
        minHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,
        maxHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,
      },
    },
  },

  muiTableBodyCellProps: {
    sx: {
      boxSizing: "border-box",

      height: `${PV_TABLE_ROW_HEIGHT}px !important`,
      minHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,
      maxHeight: `${PV_TABLE_ROW_HEIGHT}px !important`,

      display: "flex",
      alignItems: "center",

      minWidth: "0 !important",

      overflow: "hidden",

      borderRight: `${PV_TABLE_BORDER} !important`,

      fontSize: `${bodyFontSize}px !important`,

      "& .Mui-TableBodyCell-Content-Wrapper": {
        width: "100%",
        minWidth: 0,
        maxWidth: "100%",

        height: "100%",

        display: "flex",
        alignItems: "center",

        overflow: "hidden",
        textOverflow: "ellipsis",

        fontSize: `${bodyFontSize}px !important`,
      },
    },
  },
});
